import {
  Chip,
  CircularProgress,
  Grid2,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AttachFile,
  Person,
  UnfoldLess,
  UnfoldMore,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef } from "react";
import useUIBp from "../../../../../Hooks/useUIBoilerplate";
import { useWorker } from "../../../../../Hooks/useWorker";
import AutoHelp from "../../../../UI/Wrappers/AutoHelp";
import { getMessageBodyId } from "../../../Actions/getMessageBody";
import {
  extractFromUserName,
  safeJSONParse,
} from "../../../Actions/utils";

const UIText = {
  "es-CL": {
    attachments: "tiene archivos adjuntos",
    showFull: "Ver mensaje completo",
    showSimple: "Ver versión simplificada",
    error: "No se pudo cargar el mensaje completo",
  },
  "en-US": {
    attachments: "has attached files",
    showFull: "Show full message",
    showSimple: "Show simplified version",
    error: "Could not load the full message",
  },
};

// Shared header for original messages and replies: sender name with the
// creation date right next to it, and a toggle button (right of the date) that
// loads/collapses the full original body. Optionally shows an attachments
// indicator.
const MessageHeader = ({
  from_user,
  created_date_time,
  attachments,
  messageId,
  showFull,
  onToggleBody,
}) => {
  const { settings } = useUIBp();
  const t = UIText[settings.locale];

  const { working, error } = useWorker(getMessageBodyId(messageId));
  const prevWorking = useRef(false);

  // Surface a snackbar when a body fetch finishes with an error.
  useEffect(() => {
    if (prevWorking.current && !working && error) {
      enqueueSnackbar(`${t.error}: ${error}`, { variant: "error" });
    }
    prevWorking.current = working;
  }, [working, error, t.error]);

  const fromObj = safeJSONParse(from_user);
  const name = extractFromUserName(fromObj) || "—";

  const date = created_date_time
    ? new Date(created_date_time).toLocaleString(settings.locale)
    : "";

  const attachs = safeJSONParse(attachments, []);
  const hasAttachments = Array.isArray(attachs) && attachs.length > 0;

  const tooltip = showFull ? t.showSimple : t.showFull;

  return (
    <Grid2
      container
      {...{ size: 12, columnGap: 1, rowGap: 0.5, alignItems: "center" }}
    >
      <Chip icon={<Person />} label={name} size="small" />
      <Typography variant="caption" color="text.secondary">
        {date}
      </Typography>
      {messageId ? (
        <AutoHelp {...{ fixedTooltip: tooltip }}>
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={working}
              onClick={onToggleBody}
              aria-label={tooltip}
            >
              {working ? (
                <CircularProgress size={16} />
              ) : showFull ? (
                <UnfoldLess sx={{ fontSize: 18 }} />
              ) : (
                <UnfoldMore sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </AutoHelp>
      ) : null}
      {hasAttachments ? (
        <AutoHelp {...{ fixedTooltip: t.attachments }}>
          <AttachFile sx={{ fontSize: 18 }} color="primary" />
        </AutoHelp>
      ) : null}
    </Grid2>
  );
};

export default MessageHeader;
